"use client";

import React, { FC, memo, useMemo } from "react";
import { cn } from "utils";
import { Icon, ospThemes } from "@ospweb/sdk-ui";
import Translations from "@translate/components/CoPilot/360Reports/ReportItem/ReportRatingChange";
import { I18nUtil } from "@utils/i18n";

const I18n = I18nUtil.createScoped(Translations);

interface ReportRatingChangeProps {
  /** 评级变化方向：up 表示上升，down 表示下降 */
  ratingChange: "up" | "down";
  /** 之前的评分（用于显示变化，如 A） */
  previousScore?: string;
  /** 最终评分（如 A+） */
  finalScore: string;
  /** 自定义类名 */
  className?: string;
}

/**
 * ReportRatingChange 评级变化组件
 * 显示评级变化文本和标签
 *
 * @param props ReportRatingChangeProps
 * @returns React.ReactElement
 */
export const ReportRatingChange: FC<ReportRatingChangeProps> = memo(
  ({ ratingChange, previousScore, finalScore, className }) => {
    const ratingChangeText = useMemo(
      () =>
        ratingChange === "up" ? I18n.t("Rating up") : I18n.t("Rating down"),
      [ratingChange],
    );

    const ratingChangeColor = useMemo(
      () =>
        ratingChange === "up"
          ? "text-content_accent1_primary"
          : "text-content_accent2_primary",
      [ratingChange],
    );

    const tagBgColor = useMemo(
      () =>
        ratingChange === "up"
          ? "bg-background_accent1_overlay20"
          : "bg-background_accent2_overlay20",
      [ratingChange],
    );

    const iconColor = useMemo(
      () =>
        ratingChange === "up"
          ? ospThemes.colors.content_accent1_primary
          : ospThemes.colors.content_accent2_primary,
      [ratingChange],
    );

    // 如果没有 previousScore，只显示 finalScore
    if (!previousScore) {
      return (
        <div className={cn("flex items-center gap-1", className)}>
          <span className={cn("body_medium_s", ratingChangeColor)}>
            {ratingChangeText}
          </span>
          <div className={cn("px-1 py-0.5 rounded-1", tagBgColor)}>
            <span className={cn("body_bold_s", ratingChangeColor)}>
              {finalScore}
            </span>
          </div>
        </div>
      );
    }

    return (
      <div className={cn("flex items-center gap-1", className)}>
        <span className={cn("body_medium_s", ratingChangeColor)}>
          {ratingChangeText}
        </span>
        {/* 评级标签 */}
        <div
          className={cn("px-1 rounded-1 flex items-center gap-0.5", tagBgColor)}
        >
          <span className={cn("body_bold_s", ratingChangeColor)}>
            {previousScore}
          </span>
          <Icon name="right-arrow-alt" size={12} color={iconColor} />
          <span className={cn("body_bold_s", ratingChangeColor)}>
            {finalScore}
          </span>
        </div>
      </div>
    );
  },
);

ReportRatingChange.displayName = "ReportRatingChange";
